import classNames from "classnames";
import * as luxon from "luxon";
import { useContext } from "react";
import { useParams } from "react-router";
import { ClipLoader } from "react-spinners";
import { Constants } from "../../../../../common/constants";
import { time_format_utils } from "../../../../../common/time_format_utils";
import { RouterInferenceHelpers } from "../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../dashboard_utils";
import { ErrorTooltip } from "../../forms/improved_components/tooltips/error_tooltip";
import { trpc } from "../../utils/trpc";
import { GuildSettingsContext } from "../guild_settings";

export function GuildSyncsSection(props: { onBackClicked: () => void }) {
	const { server_id } = useParams<{ server_id: string }>();

	const guild_syncs_query = trpc.gcal.getGuildSyncs.useQuery({
		guild_id: server_id,
	});

	return (
		<div className="flex flex-col gap-2">
			<h1 className="has-text-weight-bold is-size-5 mx-auto pt-4">
				All Inbound Syncs
			</h1>
			{guild_syncs_query.isSuccess ? (
				<GuildSyncsList guild_syncs={guild_syncs_query.data} />
			) : (
				<div className="flex justify-center">
					<ClipLoader
						size={"2rem"}
						loading={true}
						color={Constants.EMBED_COLOR}
					/>
				</div>
			)}
			<a
				href="#"
				className="secondary-link flex items-center gap-2"
				onClick={() => props.onBackClicked()}
			>
				<i className="fas fa-chevron-left"></i>Back
			</a>
		</div>
	);
}

function GuildSyncsList(props: {
	guild_syncs: RouterInferenceHelpers["gcal"]["getGuildSyncs"]["output"];
}) {
	return (
		<table className="sesh-table listing-table w-full">
			<thead>
				<tr>
					<th className="w-12"></th>
					<th>Sync Creator</th>
					<th>Channel</th>
					<th>Last Sync</th>
					<th className="w-12"></th>
				</tr>
			</thead>
			<tbody>
				{props.guild_syncs.length > 0 ? (
					props.guild_syncs.map((val, index) => (
						<GuildSyncsListRow key={index} sync={val} />
					))
				) : (
					<td className="has-text-centered" colSpan={5}>
						None
					</td>
				)}
			</tbody>
		</table>
	);
}

function GuildSyncsListRow(props: {
	sync: RouterInferenceHelpers["gcal"]["getGuildSyncs"]["output"][number];
}) {
	const { sync } = props;

	const utils = trpc.useContext();

	const { all_text_channels } = useContext(GuildSettingsContext);

	const deleteSyncMutation = trpc.gcal.deleteSync.useMutation({
		onSuccess: () => {
			utils.gcal.getSyncsOrAuthURL.invalidate();
			utils.gcal.getGuildSyncs.invalidate();
		},
	});

	const channel_name = `#${
		all_text_channels.find(
			(channel) => sync.output_channel_id === channel.id
		)?.name ?? "unknown"
	}`;

	return (
		<tr>
			<td className="has-text-centered">
				{sync.error_code ? (
					<ErrorTooltip
						info={
							<div className="flex w-full max-w-full rounded-sm text-sm">
								Status Code: {sync.error_code}
								<br />
								{sync.error_message}
							</div>
						}
					></ErrorTooltip>
				) : (
					<i
						className="fas fa-check-circle success-text"
						title="Active"
					></i>
				)}
			</td>
			<td className="truncate" title={sync.user.id}>
				{sync.user.name}
			</td>
			<td className="truncate">{channel_name}</td>
			<td>
				{time_format_utils.format_relative_date_concise(
					luxon.DateTime.fromJSDate(
						new Date(sync.last_synced_at_time)
					)
				)}
			</td>
			<td>
				<div className="flex">
					<button
						className={classNames(
							"error-link mx-auto select-none border-none !bg-transparent p-1",
							{
								"is-loading": deleteSyncMutation.isLoading,
							}
						)}
						onClick={() => {
							DashboardUtils.ShowDeleteConfirmAlert(
								<div className="flex flex-col items-center gap-4">
									<span>
										Are you sure you want to delete this
										sync?
									</span>
									<div className="text-sm font-bold">
										Author: {sync.user.name}
										<br />
										Channel: {channel_name}
									</div>
								</div>
							).then((val) => {
								if (!val.isConfirmed) {
									return;
								}
								deleteSyncMutation.mutate({
									sync_id: sync.id,
								});
							});
						}}
					>
						<i className={"far fa-trash-alt ml-auto"}></i>
					</button>
				</div>
			</td>
		</tr>
	);
}
